<template>
  <v-container>
    <back-button
      @click="replaceToRoute('SettlementDetails', {settlementId: settlementId})"
      :label="(settlement ? (getMonthNameByIndex(settlement.month) + ' ' +  settlement.year) : '') + ' ' + transSingular('settlement.settlement')"
    ></back-button>
    <page-heading>{{ pageTitle }}</page-heading>

    <request-state :request-promise="settlementPromise">
      <v-card>
        <v-card-text>
          <info-alert
            v-if="settlementObjectsReadyToBeSentToOwners"
            class="mb-6"
          >
            {{ transc('settlement.sendToOwnersHelp', settlementObjectsReadyToBeSentToOwners && settlementObjectsReadyToBeSentToOwners.count ? settlementObjectsReadyToBeSentToOwners.count : 0) }}
          </info-alert>

          <form-input
              v-if="this.objects"
              :label="transc('object.object', this.objects.length)"
          >
            <template v-slot:input>
              <div v-for="object in objects" :key="object.id">
                <object-name :value="object"/>
              </div>
            </template>
          </form-input>

          <form-input
            :label="trans('emailSettings.fromEmail')"
            v-model="emailSettings.fromEmail"
          ></form-input>
          <form-input
            :label="trans('emailSettings.fromName')"
            v-model="emailSettings.fromName"
          ></form-input>
          <form-input
            :label="trans('emailSettings.cc')"
            v-model="emailSettings.cc"
          ></form-input>
          <form-input
            :label="trans('emailSettings.bcc')"
            v-model="emailSettings.bcc"
          ></form-input>
          <form-input
            class="mt-6"
            :label="trans('emailSettings.subject')"
            v-model="emailSettings.subject"
          ></form-input>
          <form-input-template-settlement-cover-letter
            :label="trans('emailSettings.body')"
            :text.sync="emailSettings.body"
            :closing-name="emailSettings.closingName"
          >
            <template #content-append>
              <hr class="mt-5">
            </template>
          </form-input-template-settlement-cover-letter>
          <form-input
            :label="trans('emailSettings.footer')"
            disabled
          >
            <template v-slot:input>
              <div v-if="emailSettings.footer" v-html="emailSettings.footer.replace(/\n/g, '<br>')"></div>
              <div v-else>&ndash;</div>
            </template>
          </form-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <cancel-button></cancel-button>
          <v-btn
            color="primary"
            @click="requestSettlementSendToOwners"
            :loading="settlementSendToOwnersLoading"
            :disabled="!(settlementObjectsReadyToBeSentToOwners && settlementObjectsReadyToBeSentToOwners.count > 0)"
          >
            <v-icon left>mdi-email-send-outline</v-icon>
            {{ trans('settlement.sendToOwners') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </request-state>
  </v-container>
</template>

<script>
import {MainUtils} from '../utils/main-utils';
import {LocaleUtils} from '../utils/locale-utils';
import {RouterUtils} from '../utils/router-utils';
import {DateUtils} from '../utils/date-utils';
import FormInput from '../components/FormInput';
import CancelButton from '../components/CancelButton';
import {SettlementApi} from '../api/settlement-api';
import BackButton from '../components/BackButton';
import PageHeading from '../components/PageHeading';
import RequestState from '../components/RequestState';
import {UserApi} from '../api/user-api';
import InfoAlert from '../components/InfoAlert';
import ObjectName from '@/components/ObjectName';
import FormInputTemplateSettlementCoverLetter from '@/components/FormInputTemplateSettlementCoverLetter';

export default {
  name: 'SettlementSendToOwnersPage',
  mixins: [
    MainUtils,
    LocaleUtils,
    RouterUtils,
    DateUtils
  ],
  components: {
    FormInputTemplateSettlementCoverLetter,
    ObjectName, InfoAlert, RequestState, PageHeading, BackButton, FormInput, CancelButton},
  props: {
    settlementId: {
      required: true
    },
    objects: {
      type: Array
    }
  },
  data () {
    return {
      /** @type {SettlementDto} */
      settlement: null,
      emailSettings: {
        fromEmail: null,
        fromName: null,
        closingName: null,
        cc: null,
        bcc: null,
        subject: null,
        body: null,
        footer: ''
      },
      settlementPromise: null,
      settlementSendToOwnersLoading: false,
      /** @type SettlementSendToOwnersRequestDto */
      settlementObjectsReadyToBeSentToOwners: null
    }
  },
  mounted () {
    this.getSettlement();
    this.getSettlementObjectsReadyToBeSentToOwners();
    this.getEmailSettings();
  },
  methods: {
    getSettlement () {
      this.settlementPromise = SettlementApi.getById(this.settlementId);
      this.settlementPromise.then((response) => {
        this.settlement = response.body;
      })
    },
    getEmailSettings () {
      UserApi.getEmailSettings().then((response) => {
        this.emailSettings.fromEmail = response.body.fromEmail;
        this.emailSettings.fromName = response.body.fromName;
        this.emailSettings.closingName = response.body.closingName;
        this.emailSettings.footer = response.body.footer;
      })
      UserApi.getUserSettings().then((response) => {
        this.emailSettings.body = response.body.templateSettlementCoverLetter;
      })
    },
    getSettlementObjectsReadyToBeSentToOwners () {
      SettlementApi.getSettlementObjectsReadyToBeSentToOwners(this.settlementId, this.objectIdsFilter)
      .then(response => this.settlementObjectsReadyToBeSentToOwners = response.data)
      .catch(this.handleErrorResponse);
    },
    requestSettlementSendToOwners () {
      this.settlementSendToOwnersLoading = true;
      let settlementSendToOwnersRequest = Object.assign({}, this.emailSettings);
      settlementSendToOwnersRequest['objectIds'] = this.objectIdsFilter;
      delete settlementSendToOwnersRequest['footer'];
      SettlementApi.sendToOwners(this.settlement.id, settlementSendToOwnersRequest)
      .then(response => this.replaceToRoute('SettlementDetails', {settlementId: this.settlement.id}))
      .catch(this.handleErrorResponse)
      .finally(() => this.settlementSendToOwnersLoading = false);
    }
  },
  watch: {
    settlementId () {
      this.getSettlement();
    },
    settlement (settlement) {
      if (this.settlement) {
        this.emailSettings.subject = this.trans('settlement.sendToOwnerSubject', {
          monthName: this.getMonthNameByIndex(settlement.month),
          year: this.settlement.year
        }) + ' - {ObjektName}';
      } else {
        this.emailSettings.subject = null;
      }
    }
  },
  computed: {
    pageTitle () {
      if (this.settlement) {
        return this.getMonthNameByIndex(this.settlement.month) + ' ' +  this.settlement.year + ' - ' + this.transSingular('settlement.settlement', 1) + ' - ' + this.trans('settlement.sendToOwners');
      }
      return null;
    },
    objectIdsFilter () {
      if(this.objects == null) {
        return null;
      }

      if(Array.isArray(this.objects)) {
        return this.objects.map(obj => obj.id);
      }

      return [this.objects.id];
    }
  }
}
</script>

